<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="fas fa-clipboard-check" class="q-mb-sm" /> {{ $t('courses_catalog') }}
        </div>

        <div class="row gutter-sm">
            <div class="col-12">
                <q-field :count="256">
                    <q-input v-model="title" :float-label="$t('course_name')" :error="$v.title.$error" @input="$v.title.$touch" :disable="disableEditCatalog" />
                </q-field>
            </div>

            <div class="col-12">
                <q-field :count="1024">
                    <q-input v-model="description" type="textarea" :error="$v.description.$error" @input="$v.description.$touch" :float-label="$t('description')" :disable="disableEditCatalog" />
                </q-field>
            </div>

            <div class="col-xs-12 col-sm-12">
                <q-field :helper="$t('training_code_helper_message')">
                    <q-input type="text" v-model="code" :error="$v.code.$error" @input="$v.code.$touch" :float-label="$t('training_code')" :disable="disableEditCatalog" />
                </q-field>
            </div>

            <div class="col-xs-12 col-sm-12">
                <inputExpireCertificate
					v-if="selectedClientParams.use_presential_events_certificate_expiration"
                    :disable="disableEditCatalog"
					@fnInDays="fnInDays"
					:qty_days="qty_days"
				/>
            </div>

            <div class="col-xs-12 col-sm-12 q-mt-md">
                <p class="caption q-mb-xs">{{ $t('categories_modules') }}</p>

                <q-checkbox class="q-mb-xs" v-model="all_product_categories" :val="true" :label="$t('select_all_categories_modules')" @input="checkAllCategories()" :disable="disableEditCatalog" />

                <q-select v-model="product_categories" ref="product_categories" :options="product_categories_list.map(e => { return { value: e.value, label: e.title }})" multiple chips filter :error="$v.product_categories.$error" @input="changeProducts" :disable="disableEditCatalog" />
            </div>

            <div class="col-xs-12 col-sm-12 q-mt-md">
                <p class="caption q-mb-xs">{{ $t('products_themes') }}</p>

                <q-checkbox class="q-mb-xs" v-model="all_products" :val="true" :label="$t('select_all_product_themes')" @input="checkAllProducts()" :disable="product_categories.length < 1 || disableEditCatalog" />

                <q-select v-model="products" ref="products" :options="filteredProducts" multiple chips filter :error="$v.products.$error" @input="$v.products.$touch" :disable="disableEditCatalog" />
            </div>

            <div class="col-xs-12 col-sm-12">
                <q-select v-model="tags" :options="optionTags" :float-label="$t('tags')" multiple chips filter autofocus-filter :disable="disableEditCatalog" />
            </div>

            <div class="col-xs-12 col-sm-12">
                <p class="caption">{{ $t('areas') }}</p>

                <q-checkbox class="q-mb-sm" v-model="all_areas" :val="true" :label="$t('select_all_areas')" @input="checkAllAreas()" :disable="disableEditCatalog" />

                <q-select filter autofocus-filter v-model="area" ref="area" :options="areas" :error="$v.area.$error" @input="$v.area.$touch" multiple chips :disable="disableEditCatalog" />
            </div>

            <div class="col-xs-12 col-sm-12">
                <p class="caption">{{ $t('profiles') }}</p>

                <div class="row gutter-sm">
                    <div class="col-xs-12 col-sm-12">
                        <q-checkbox v-model="all_profiles" :val="true" :label="$t('all')" @input="checkAll()" :disable="disableEditCatalog" />
                    </div>

                    <div class="col-xs-12 col-sm-4" v-for="(item, index) in profiles" :key="index" :class="getErrorClass($v.profile.$error)">
                        <q-checkbox keep-color v-model="profile" ref="profile" :val="item.value" :label="item.label" @input="$v.profile.$touch" :color="getErrorColor($v.profile.$error)" :disable="disableEditCatalog" />
                    </div>
                </div>
            </div>
        </div>

        <div class="row gutter-sm justify-end q-mt-lg">
            <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                <q-btn color="tertiary" class="full-width" :label="$t('back')" icon="fas fa-chevron-left" @click="$router.push({ name: 'courses_catalog' })" />
            </div>

            <div class="col-xs-12 col-sm-3" :class="getOrder('save')">
                <q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveData()" :disable="disableEditCatalog" />
            </div>
        </div>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, minValue, maxLength } from 'vuelidate/lib/validators'
import InputExpireCertificate from 'components/admin/shared/InputExpireCertificate'

export default {
    name: 'AdminCourseCatalog',

    i18nOptions: { namespaces: ['custom', 'common', 'presential', 'admin', 'training'] },

    data() {
        return {
            all_areas: false,
            all_product_categories: false,
            all_products: false,
            all_profiles: false,
            area: [],
            code: '',
            description: '',
            id: '',
            id: '',
            product_categories: [],
            products: [],
            profile: [],
            tags: [],
            title: '',
            qty_days_result_expiration: 0,
            qty_days: 0
        }
    },

    components: {
		inputExpireCertificate: InputExpireCertificate,
	},

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            areas: 'registration_lists/country_areas',
            id_user_client_country_selected: 'common/id_user_client_country_selected',
            id_user_language_selected: 'common/id_user_language_selected',
            product_categories_list: 'registration_lists/product_categories',
            products_list: 'registration_lists/products',
            profiles: 'registration_lists/profiles',
            tags_list: 'registration_lists/tags',
            user_module_items: 'common/user_module_items',
            id_user_client_selected: 'common/id_user_client_selected',
			clients: 'params/clients',
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        optionTags() {
            if(this.tags_list.length > 0) {
                return this.tags_list.map(t => {
                    let areas = t.areas.map(a => a.name)
                    return {
                        value:t.value,
                        label: t.label,
                        sublabel: areas.join(', ')
                    }
                })
            }
            else return []
        },

        filteredProducts() {
            return this.products_list ? this.products_list.filter(p => this.product_categories.includes(p.product_category)) : []
        },

        disableEditCatalog() {
            let client = this.clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			return !client.presential_event_catalog
        },
        selectedClientParams() {
            let client = this.clients.find((obj) => {
                return obj.id === this.id_user_client_selected
            })
            return client ? client : {}
        }
    },

    methods: {
        fetchData() {
            this.$q.loading.show()

            this.$axios.get(`/presential_manager/admin_presential_catalog/${this.id}/`, this.getHeader)
            .then(response => {
                this.title = response.data.title
                this.description = response.data.description
                this.code = response.data.code
                this.product_categories = response.data.product_categories
                this.products = response.data.products
                this.tags = response.data.tags
                this.area = response.data.areas
                this.profile = response.data.profiles
                this.qty_days = response.data.qty_certificate_expiration_days
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        saveData() {
            this.$v.$touch()

            if (!this.$v.$error) {
                this.$q.loading.show()

                let data = {
                    'title': this.title,
                    'description': this.description,
                    'code': this.code,
                    'product_categories': this.product_categories,
                    'products': this.products,
                    'tags': this.tags,
                    'areas': this.area,
                    'profiles': this.profile,
                    'qty_certificate_expiration_days': this.qty_days_result_expiration != null || this.qty_days_result_expiration !== 0 ? this.qty_days_result_expiration : null
                }

                if (this.id != 0) {
                    this.$axios.put(`/presential_manager/admin_presential_catalog/${this.id}/`, data, this.getHeader)
                    .then(response => {
                        this.$q.notify({
                            message: this.$t('course_catalog_save_success'),
                            type: 'positive',
                            icon: 'far fa-save'
                        })

                        this.$router.push({ name: 'courses_catalog' })
                    })
                    .catch(e => {
                        console.log(e)

                        this.$q.notify({
                            message: this.$t('course_catalog_save_error'),
                            type: 'negative',
                            icon: 'fas fa-ban'
                        })
                    })
                    .finally(() => {
                        this.$q.loading.hide()
                    })
                }
                else {
                    this.$axios.post(`/presential_manager/admin_presential_catalog/`, data, this.getHeader)
                    .then(response => {
                        this.$q.notify({
                            message: this.$t('course_catalog_save_success'),
                            type: 'positive',
                            icon: 'far fa-save'
                        })

                        this.$router.push({ name: 'courses_catalog' })
                    })
                    .catch(e => {
                        console.log(e)

                        this.$q.notify({
                            message: this.$t('course_catalog_save_error'),
                            type: 'negative',
                            icon: 'fas fa-ban'
                        })
                    })
                    .finally(() => {
                        this.$q.loading.hide()
                    })
                }
            }
        },

        fnInDays(value) {
			this.qty_days_result_expiration = value
		},

        getOrder(name) {
            if (name == 'cancel') {
                if (this.$q.platform.is.mobile) return 'order-last'
                else return 'order-first'
            }
            else if (name == 'save') {
                if (this.$q.platform.is.mobile) return 'order-first'
                else return 'order-last'
            }
        },

        changeProducts() {
            this.$v.product_categories.$touch()
            this.products = this.products.filter(p => this.filteredProducts.find((fp) => { return fp.value === p }))
        },

        checkAll() {
            if (this.all_profiles) {
                this.profiles.forEach(element => {
                    this.profile.push(element.value)
                })
            }
            else {
                this.profile = []
            }
        },

        checkAllAreas() {
            if(this.all_areas) {
                this.area = this.areas.map(e => e.value)
            }
            else {
                this.area = []
            }
        },

        checkAllCategories() {
            if(this.all_product_categories) {
                this.product_categories = this.product_categories_list.map(e => e.value)
            }
            else {
                this.product_categories = []
            }
        },

        checkAllProducts() {
            if(this.all_products) {
                this.products = this.filteredProducts.map(e => e.value)
            }
            else {
                this.products = []
            }
        },

        getErrorClass(state) {
            if (state) return 'checkbox-error'
            else return ''
        },

        getErrorColor(state) {
            if (state) return 'negative'
            else return ''
        }
    },

    validations() {
        return {
            area: {
                required
            },
            code: {
                required
            },
            description: {
                maxLength: maxLength(1024)
            },
            product_categories: {
                required
            },
            products: {
                required
            },
            profile: {
                required
            },
            title: {
                maxLength: maxLength(256),
                required
            }
        }
    },

    created() {
        this.id = this.$route.params.id
        if (this.id != 0) this.fetchData()
    }
}
</script>

<style>

</style>
